import React from 'react';
import * as Sentry from '@sentry/browser';
import { useDebounceEffect } from './utils';
import { Button, CheckboxLabel, Section, Date, Input, Modal, InputLabel, OptionalSection, Form, Message, ToastContext, DateTimeInput } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useForm } from '../../../../utils/validators';
import i18n from 'i18next';
import moment from 'moment-timezone';
import { fetcher } from '@weezevent/weezjs-core';
import EventModel from '../../../../models/eventModel';
import Minisite from '../../../../models/minisite';
import Style from '../eventCard.module.css';
import { useSegment } from '../../../../components/tracking/trackingProvider';
import { getLocalizationTypeEvent } from '../../../../components/template/contextMenu/event-context-menu';

export default function DuplicateModal({ event, allEvents, open, onClose, resetPagination, organization }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const toast = React.useContext(ToastContext);
    const [selectedOptions, setSelectedOptions] = React.useState(new Set(['duplicate_rates', 'duplicate_forms', 'duplicate_discount_code', 'duplicate_contacts']));
    const [canSubmit, setCanSubmit] = React.useState(false);
    const [isSubmiting, setIsSubmiting] = React.useState(false);
    const [sameEventName, setSameEventName] = React.useState(false);

    const validateStartDate = React.useCallback((name, date, dateForm) => {
        if (dateForm.start_date) {
            return true;
        }
        return 'ticket.event.no_date_start';
    }, []);

    const validateDate = React.useCallback((name, date, dateForm) => {
        if (!dateForm.start_date) {
            return;
        }
        let startTzEvent = moment.tz(dateForm.start_date, dateForm.timezone);

        if (dateForm.end_date) {
            let endTzEvent = moment.tz(dateForm.end_date, dateForm.timezone);
            if (startTzEvent.isAfter(endTzEvent)) {
                return 'ticket.event.dateend_before_error';
            }
        }
        return true;
    }, []);

    const defaultStartDate = React.useMemo(() => {
        return moment
            .utc()
            .add(3, 'hours')
            .set('minute', 0)
            .set('second', 0)
            .format();
    }, []);

    const [form, setForm, , formErrors, validateForm] = useForm(
        {
            event_name: event.name,
            start_date: defaultStartDate,
            end_date: null,
            duplicate_rates: true,
            duplicate_forms: true,
            duplicate_discount_code: true,
            duplicate_contacts: true,
            duplicate_sessions: true,
            duplicate_minisite: false
        },
        {
            start_date: validateStartDate,
            end_date: validateDate
        }
    );

    const locale = i18n.language;

    const currentYear = new Date().getFullYear();

    const options = [
        {
            name: 'duplicate_rates',
            value: 'duplicate_rates'
        },
        {
            name: 'duplicate_forms',
            value: 'duplicate_forms'
        },
        {
            name: 'duplicate_discount_code',
            value: 'duplicate_discount_code'
        },
        {
            name: 'duplicate_contacts',
            value: 'duplicate_contacts'
        },
        {
            name: 'duplicate_minisite',
            value: 'duplicate_minisite'
        }
    ];

    if (event.use_sessions) {
        selectedOptions.add('duplicate_sessions');
        options.push({
            name: 'duplicate_sessions',
            value: 'duplicate_sessions'
        });
    }

    const handleChange = React.useCallback(
        (e, { value, checked }) => {
            let tmpCheckedOptions = new Set(selectedOptions);
            if (!checked) {
                tmpCheckedOptions.delete(value);
            } else {
                tmpCheckedOptions.add(value);
            }
            setSelectedOptions(tmpCheckedOptions);
            setForm(value, checked);
        },
        [setSelectedOptions, selectedOptions]
    );

    const selectAll = React.useCallback(
        (event, { checked }) => {
            if (!checked) {
                setSelectedOptions(new Set());
            } else {
                let tmpCheckedOptions = new Set();
                options.map(option => {
                    tmpCheckedOptions.add(option.value);
                    return tmpCheckedOptions;
                });
                setSelectedOptions(tmpCheckedOptions);
            }
            options.forEach(option => setForm(option.name, checked));
        },
        [options]
    );

    // Check if at least one active or draft event has the same name
    const isSameEvent = (event, name) => event.name === name && (event.publication_status === 'draft' || event.publication_status === 'published');
    const checkSameEventName = eventName => {
        setSameEventName(allEvents.some(e => isSameEvent(e, eventName)));
    };

    useDebounceEffect(
        () => {
            form.event_name && checkSameEventName(form.event_name);
        },
        500,
        [form.event_name, allEvents, checkSameEventName]
    );

    const handleDuplicateTrack = React.useCallback(() => {
        track('WEEZTICKET > ACTION > DUPLICATE EVENT ', {
            'Page Title': 'EVENT LIST',
            'Event Name': event.event_name,
            'Event Type name': event.subtype,
            'Event Start Date': event.start_date,
            'Event End Date': event.end_date,
            'Place Type': getLocalizationTypeEvent(event)
        });
    }, [event, form, track, getLocalizationTypeEvent]);

    const onSubmit = React.useCallback(() => {
        setIsSubmiting(true);
        handleDuplicateTrack();
        fetcher
            .post(`/ticket/organizations/${organization.id}/events/${event.id}/duplicate`, {
                data: {
                    name: form.event_name,
                    contact: form.duplicate_contacts,
                    form: form.duplicate_forms,
                    rates: form.duplicate_rates,
                    discount: form.duplicate_discount_code,
                    minisite: form.duplicate_minisite,
                    ...(event.use_sessions && { sessions: form.duplicate_sessions }),
                    start_date: form.start_date,
                    end_date: form.end_date
                }
            })
            .then(({ response }) => {
                toast.success(t('eventList.duplicateModal.toast-success'));
                onClose();
                setIsSubmiting(false);
                // we put timeout here because duplication of events in weez is asynchronous and takes few seconds to create the new event
                setTimeout(() => {
                    resetPagination();
                    // Do not generate a minisite in the case of a duplicate minisite
                    // because this is handled in Gemino and it may cause issues.
                    if (form.duplicate_minisite) return;
                    const minisite = new Minisite();
                    minisite
                        .generate({ organizationId: organization.id, eventId: response.id_evenement })
                        .then(r => {
                            let eventObject = new EventModel({
                                id: response.id_evenement,
                                site: r.id,
                                gemino_url: r.url
                            });
                            eventObject
                                .save({
                                    organizationId: organization.id
                                })
                                .catch(e => {
                                    Sentry.captureException(e);
                                    toast.error(t('eventList.duplicateModal.toast-error-save'));
                                })
                                .finally(() => {
                                    setIsSubmiting(false);
                                });
                        })
                        .catch(e => {
                            Sentry.captureException(e);
                            toast.error(t('eventList.duplicateModal.toast-error'));
                            setIsSubmiting(false);
                        });
                }, 5000);
            })
            .catch(() => {
                toast.error(t('eventList.duplicateModal.toast-error'));
                setIsSubmiting(false);
            });
    }, [event, organization, form, toast, resetPagination, onClose, handleDuplicateTrack]);

    React.useEffect(() => {
        setCanSubmit(validateForm());
    }, [form]);

    return (
        <Modal
            size="medium"
            allowExternalClosing={false}
            open={open}
            onClose={onClose}
            submitButton={<Button primary label={t('ticket.common.action.duplicate')} onClick={onSubmit} disabled={!canSubmit} isLoading={isSubmiting} />}
            cancelButton={<Button onClick={onClose} label={t(`ticket.common.action.cancel`)} disabled={isSubmiting} />}
            title={t('eventList.duplicateModal.title')}
            subtitle={t('eventList.duplicateModal.subtitle', { name: event?.name })}
        >
            {sameEventName && (
                <Message type="default" title={t('eventList.duplicateModal.same-name-title')}>
                    {t('eventList.duplicateModal.same-name-description')}
                </Message>
            )}
            <Form.Field>
                <div>
                    <InputLabel label={t('eventList.duplicateModal.event-name')} />
                    <Input
                        placeholder={t('eventList.duplicateModal.event-name-placeholder', {
                            event_name: event?.name
                        })}
                        value={form.event_name}
                        onChange={(e, { value }) => setForm('event_name', value)}
                    />
                    <div className={Style['remaining-characters']}>{t('eventList.duplicateModal.nb-char', { number: 80 - form.event_name.length })}</div>
                </div>
                <div className={Style['dates-container']}>
                    <div className={Style['date-inputs']}>
                        <Section>
                            <DateTimeInput
                                className={Style['date-time-input']}
                                timeLabel={t('ticket.event.start_hour')}
                                dateLabel={t('ticket.event.start_date')}
                                onChange={date => {
                                    setForm('start_date', date);
                                }}
                                value={form.start_date}
                                dateProps={{ minYear: currentYear - 7, maxYear: currentYear + 15 }}
                                locale={locale}
                                timezone={event.timezone}
                                error={t(formErrors['start_date'])}
                            />
                        </Section>
                    </div>
                    <OptionalSection className={Style['optional-section']} label={t('eventList.duplicateModal.optional-section-label')}>
                        <div className={Style['date-inputs']}>
                            <Section>
                                <DateTimeInput
                                    className={Style['date-time-input']}
                                    timeLabel={t('ticket.event.end_hour')}
                                    dateLabel={t('ticket.event.end_date')}
                                    onChange={date => {
                                        setForm('end_date', date);
                                    }}
                                    value={form.end_date}
                                    dateProps={{ minYear: currentYear - 7, maxYear: currentYear + 15, minDate: form.start_date }}
                                    locale={locale}
                                    timezone={event.timezone}
                                    error={t(formErrors['end_date'])}
                                />
                            </Section>
                        </div>
                    </OptionalSection>
                </div>
                <div>
                    <div className={Style['duplicate-checkbox-info']}>{t('eventList.duplicateModal.checkbox-choice')}</div>
                    <div className={Style['duplicate-checkbox']}>
                        <CheckboxLabel label={t('ticket.common.action.select-all')} onChange={selectAll} checked={selectedOptions.size === options.length} />
                    </div>
                    {options.map(option => {
                        return (
                            <CheckboxLabel
                                key={option.value}
                                label={t(`eventList.duplicateModal.${option.name}`)}
                                onChange={handleChange}
                                value={option.value}
                                checked={selectedOptions.has(option.value)}
                            />
                        );
                    })}
                </div>
            </Form.Field>
        </Modal>
    );
}
